import { FC, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldsRow,
  FormSelect,
  FormTextField,
  FormTimePicker,
  SelectMenuWithFooter,
} from '../../../../../../shared/components/form';
import { NoteButton } from '../../../../../../shared/components/form/buttons/noteButton.component';
import {
  DictTextBlocksRes,
  DictTourplansRes,
} from '../../../../../../shared/services/configsData/configsData.service';
import { useVisibilityGUID } from '../../../../../../shared/hooks/useVisibilityGUID';
import { additionalNoteCompanies } from '../../../../../customer/components/tabs/forms/components/formsForm.component.tsx';
import { useFormContext } from 'react-hook-form';
import { NotePopup } from '../../../../common/popups/note.popup';
import { valueHelper } from '../../../../../../shared/helpers/valueProcessor/value.helper';
import { IconButton } from '../../../../common/iconButton/iconButton.component.tsx';
import TuneIcon from '@mui/icons-material/Tune';
import { ICustomer } from '../../../../loaders/editOrder.loader.ts';
import { C_Order_Type } from '../../../../../../graphql/generatedModel.ts';
import { isChashAssistOrder } from '../../orderList/orderList.row.ts';
import { MoreControlsPopup } from './popups/moreControls.popup.tsx';
import { Badge, styled, useMediaQuery } from '@mui/material';
import { orderDetailsState } from '../../../states/orderDetails/orderDetails.state.ts';

export const SideControls: FC<ISideControls> = ({
  linkTextOptions,
  tourplanOptions,
  errorText,
  customerList,
  orderType,
  isEditable,
  isNotePinned,
  isEmptyTourError,
  setIsEmptyTourError,
}) => {
  const { t } = useTranslation();
  const showAdditionalNote = useVisibilityGUID(additionalNoteCompanies);
  const isFullHd = useMediaQuery('(min-width:1920px)');
  const { getValues } = useFormContext();
  const notePopup = useMemo(
    () => new NotePopup({ isEditable, showAdditionalNote }),
    [showAdditionalNote, isEditable],
  );

  const isNote = useMemo(
    () =>
      getValues('notePlainText') ||
      valueHelper.checkValueInHtml(getValues('note')) ||
      (showAdditionalNote && valueHelper.checkValueInHtml(getValues('note2'))),
    [getValues(), showAdditionalNote],
  );
  const isCashAssist = isChashAssistOrder(orderType);
  const moreControlsPopup = useMemo(
    () => new MoreControlsPopup({ customerList, isCashAssist, isEditable }),
    [isCashAssist, customerList, isEditable],
  );

  useEffect(() => {
    const subscription = notePopup.stream.state().subscribe((v) => {
      if (v?.action === 'updateIsNotePinned') {
        orderDetailsState.pub.updateIsNotePinned(!!v?.values?.isNotePinned, isFullHd);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [notePopup]);

  return (
    <FieldsRow direction={'row'} spacing={2} alignItems='start'>
      <FormTextField
        disabled={!isEditable}
        slotProps={{ htmlInput: { maxLength: 250 } }}
        name='referenceOrderNo'
        width='220px'
        label={t('order.reference')}
      />
      <FormSelect
        name='tourplanId'
        options={tourplanOptions as []}
        label={t('customer.tour')}
        width='220px'
        error={isEmptyTourError}
        onChange={() => setIsEmptyTourError(false)}
        PaperComponent={(props) => (
          <SelectMenuWithFooter {...props} href='/settings/configuration/customer' />
        )}
      />
      <FormTimePicker
        name='deliveryTime'
        label={t('order.delivery_time')}
        width={110}
        fieldProps={{ disabled: !isEditable, ...(errorText && { color: 'error' }) }}
        errorText={errorText && `${t('order.invalid_value')}: ${errorText}`}
      />
      <FormSelect
        disabled={!isEditable}
        name='linkText'
        options={linkTextOptions as []}
        label={t('order.text')}
        width='220px'
        PaperComponent={(props) => (
          <SelectMenuWithFooter {...props} href='/settings/print-forms/general' />
        )}
      />
      <NoteButton
        badged={isNote}
        title={t('common.note')}
        view='small'
        style={{ minWidth: 'fit-content' }}
        onClick={() =>
          notePopup.stream.emit('open', {
            values: {
              plainTextNote: getValues('notePlainText'),
              mainNote: getValues('note'),
              additionalNote: getValues('note2'),
              isNotePinned,
            },
          })
        }
      />
      <IconButton
        title={t('order.more_settings')}
        onClick={() =>
          moreControlsPopup.stream.emit('open', {
            values: {
              customerId: getValues('customerId'),
              orderDate: getValues('orderDate'),
              caCashRegisterNo: getValues('caCashRegisterNo'),
              caDeliveryMethod: getValues('caDeliveryMethod'),
              caDeliveryTime: getValues('caDeliveryTime'),
              caReceiptNo: getValues('caReceiptNo'),
              caUserName: getValues('caUserName'),
            },
          })
        }
      >
        {isCashAssist && <SBadge variant='dot' color='primary' />}
        <TuneIcon />
      </IconButton>
      <moreControlsPopup.Component />
      <notePopup.Component />
    </FieldsRow>
  );
};

const SBadge = styled(Badge)({
  position: 'absolute',
  top: '7px',
  right: '7px',
});

interface ISideControls {
  errorText: undefined | string;
  isEmptyTourError: boolean;
  setIsEmptyTourError: Dispatch<SetStateAction<boolean>>;
  linkTextOptions: DictTextBlocksRes;
  tourplanOptions: DictTourplansRes;
  customerList: ICustomer[];
  orderType: C_Order_Type;
  isEditable: boolean;
  isNotePinned: boolean;
}
